<template>
  <div class="cp-root sc-page-root">
    <FiPageRail title="Coaching Tickets" class="cp-rail">
      <template #actions>
        <!-- manual create, shared with the Coaching Board -->
        <b-button size="sm" variant="primary" title="Raise a coaching or recognition ticket"
                  aria-label="Add ticket" @click="$refs.addTicket.open()">
          <i class="fa fa-plus"></i><span class="cp-btn-label">&nbsp;Add ticket</span>
        </b-button>
        <coaching-portal-help />
      </template>
    </FiPageRail>

    <div v-if="actingTenantDiffers" class="cp-tenant-note">
      <i class="fa fa-building-o" aria-hidden="true"></i>
      Viewing <b>{{ actingTenantLabel }}</b> — not your selected fleet
    </div>

    <div v-if="loading && !tickets.length && !error" class="text-center" style="margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="loadTickets()">Retry</b-button>
    </div>

    <div v-else :class="{ 'cp-stale': loading, 'cp-filters-collapsed': !filtersExpanded }">
      <!-- search/filter bar matching the Flagged Drivers layout: date
           presets + range first, then the dropdowns, search pill on the
           right. every filter applies client-side to the loaded queue and
           composes with the others. -->
      <!-- mobile-only filters toggle (the ERM Event Log pattern, ops_desk.vue):
           collapses every filter control behind one tap so rows reach above
           the fold on a phone; the search pill stays visible. the badge
           counts active filter dimensions (search excluded — it has its own
           always-on field). display:none above 600px, so desktop is
           unaffected. -->
      <button type="button" class="cp-filter-toggle cp-mobile-only"
              :aria-expanded="filtersExpanded ? 'true' : 'false'"
              @click="filtersExpanded = !filtersExpanded">
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeFilterCount" class="cp-filter-badge">{{ activeFilterCount }}</span>
        <i class="fa" :class="filtersExpanded ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>
      <div class="cp-filter-row" role="search" aria-label="Filter coaching tickets">
        <!-- one-tap raised-in-the-last presets (the ERM od-date-presets
             pattern): fill the From picker below; tapping the active chip
             clears the range -->
        <span class="cp-filter-field" role="group" aria-label="Raised in the last">
          <button v-for="p in datePresets" :key="'dp-' + p" type="button"
                  :class="['cp-preset-chip', { 'cp-preset-chip--active': presetActive(p) }]"
                  :aria-pressed="presetActive(p) ? 'true' : 'false'"
                  @click="setDatePreset(p)">{{ p }}d</button>
        </span>
        <!-- cp-date-ghost mirrors od-date-ghost: safari renders TODAY inside
             an empty date input, which reads as an active filter — drop the
             ink to placeholder strength until a date is actually picked -->
        <span class="cp-filter-field">
          <span class="cp-filter-lbl">Raised from</span>
          <b-form-input v-model="filterFrom" type="date" size="sm"
                        :class="['cp-filter-date', { 'cp-date-ghost': !filterFrom }]" />
        </span>
        <span class="cp-filter-field">
          <span class="cp-filter-lbl">To</span>
          <b-form-input v-model="filterTo" type="date" size="sm"
                        :class="['cp-filter-date', { 'cp-date-ghost': !filterTo }]" />
        </span>
        <span class="cp-filter-field">
          <span class="cp-filter-lbl">Status</span>
          <b-form-select v-model="filterStatus" :options="statusOptions" size="sm" class="cp-filter-select" />
        </span>
        <span class="cp-filter-field">
          <span class="cp-filter-lbl">Type</span>
          <b-form-select v-model="filterKind" :options="kindOptions" size="sm" class="cp-filter-select cp-filter-select--narrow" />
        </span>
        <span class="cp-filter-field">
          <span class="cp-filter-lbl">Driver</span>
          <b-form-select v-model="filterDriver" :options="driverOptions" size="sm" class="cp-filter-select" />
        </span>
        <span class="cp-filter-field">
          <span class="cp-filter-lbl">Assigned</span>
          <b-form-select v-model="assigneeFilter" :options="assigneeOptions" size="sm" class="cp-filter-select" />
        </span>
        <span class="cp-search">
          <i class="fa fa-search cp-search-icon" aria-hidden="true"></i>
          <input v-model="filterSearch" type="text" class="cp-search-input"
                 placeholder="Search driver, topic, or #12"
                 aria-label="Search tickets by driver, topic, note, or #id" />
          <button v-if="filterSearch" type="button" class="cp-search-clear" aria-label="Clear search"
                  @click="filterSearch = ''"><i class="fa fa-times" aria-hidden="true"></i></button>
        </span>
      </div>
      <div class="cp-results-row">
        <span class="cp-results-count">
          {{ filteredTickets.length === tickets.length
            ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}`
            : `${filteredTickets.length} of ${tickets.length} tickets` }}
        </span>
        <button v-if="filtersDirty" type="button" class="cp-clear-filters" @click="clearFilters">
          <i class="fa fa-times" aria-hidden="true"></i>&nbsp;Reset filters
        </button>
      </div>

      <div class="mt-2">
          <div>
            <div v-if="!filteredTickets.length" class="text-center text-muted cp-empty">
              <p v-if="!tickets.length">The queue is clear — assign coaching from the Flagged Drivers page.</p>
              <p v-else>
                No tickets match these filters.
                <b-button size="sm" variant="outline-secondary" class="ml-2" @click="clearFilters">Reset filters</b-button>
              </p>
            </div>

            <!-- desktop table: same visual system as the ERM Event Log
                 (ops_desk.vue) — id link, pastel chip families, snippet cell,
                 sticky uppercase header, clickable rows -->
            <div v-else-if="!isMobile" class="cp-table-inline">
              <b-table :items="filteredTickets" :fields="ticketFields"
                       :tbody-tr-class="rowClass" small responsive show-empty
                       @row-clicked="openSignOff">
                <!-- keyboard path into the ticket: row-click stays for mouse
                     users, the id doubles as a real focusable control -->
                <template slot="id" slot-scope="data">
                  <button type="button" class="cp-id-link" :aria-label="'Open ticket #' + data.item.id"
                          @click.stop="openSignOff(data.item)">{{ data.item.id }}</button>
                </template>
                <template slot="kind" slot-scope="data">
                  <span class="cp-type-chip" :class="'cp-type-chip--' + (data.item.kind || 'coaching')">
                    <i class="fa" :class="data.item.kind === 'recognition' ? 'fa-star-o' : 'fa-check-square-o'"></i>
                    {{ data.item.kind === 'recognition' ? 'Recognition' : 'Coaching' }}
                  </span>
                </template>
                <template slot="driver_name" slot-scope="data">
                  <b>{{ data.item.driver_name }}</b>
                </template>
                <template slot="topic" slot-scope="data">
                  <span class="cp-snippet" :title="data.item.why || data.item.topic">
                    <b>{{ data.item.topic }}</b><span v-if="data.item.why" class="text-muted"> — {{ data.item.why }}</span>
                  </span>
                  <!-- erm-sourced rows link back to the event; plain text for
                       roles the ops_desk router guard would bounce -->
                  <div v-if="data.item.event_id" class="cp-small">
                    <router-link v-if="canViewOpsDesk"
                                 :to="{ name: 'ops_desk', query: { tenant: tenant, event_id: data.item.event_id } }"
                                 @click.native.stop>
                      event #{{ data.item.event_id }}
                    </router-link>
                    <span v-else class="text-muted">event #{{ data.item.event_id }}</span>
                  </div>
                </template>
                <template slot="source" slot-scope="data">
                  <span class="cp-cat-chip">{{ sourceLabel(data.item.source) }}</span>
                </template>
                <template slot="assigned_to_name" slot-scope="data">
                  <span v-if="data.item.assigned_to === user.username" class="cp-you-chip">You</span>
                  <span v-else class="cp-small">{{ data.item.assigned_to_name || data.item.assigned_to }}</span>
                </template>
                <template slot="due_date" slot-scope="data">
                  <span class="cp-list-dt" :class="data.item.past_due ? 'cp-list-dt--overdue' : ''">
                    {{ formatDate(data.item.due_date) }}
                  </span>
                  <div v-if="data.item.past_due" class="cp-small text-danger">{{ data.item.days_overdue }}d overdue</div>
                  <div v-else-if="data.item.age_days != null" class="cp-small text-muted">{{ data.item.age_days }}d old</div>
                </template>
                <template slot="status" slot-scope="data">
                  <span class="cp-status-chip" :class="'cp-status-chip--' + data.item.status" :title="statusLabel(data.item.status)">
                    <i class="fa" :class="statusIcon(data.item.status)"></i>
                    {{ statusLabel(data.item.status) }}
                  </span>
                  <span v-if="data.item.acknowledged_in_person" class="cp-ack-chip ml-1"
                        title="The driver acknowledged this conversation in person">
                    <i class="fa fa-handshake-o"></i> Ack
                  </span>
                  <span v-if="data.item.escalated" class="cp-sev-chip cp-sev-chip--high ml-1"
                        :title="'Overdue past the ' + slaEscalateDays + '-day escalation threshold'">Escalated</span>
                </template>
                <template slot="actions" slot-scope="data">
                  <b-button v-if="isOpenStatus(data.item.status)" size="sm" variant="primary"
                            @click.stop="openSignOff(data.item)">Sign off…</b-button>
                  <b-button v-else size="sm" variant="outline-secondary"
                            @click.stop="openSignOff(data.item)">View / add note</b-button>
                  <b-badge v-if="(data.item.notes || []).length" variant="light" class="ml-1"
                           :title="(data.item.notes || []).length + ' note(s) on file'">
                    💬 {{ (data.item.notes || []).length }}
                  </b-badge>
                </template>
              </b-table>
            </div>

            <!-- mobile card list -->
            <div v-else>
              <b-card v-for="t in filteredTickets" :key="t.id" class="mb-2 cp-mobile-card"
                      :class="{ 'cp-mobile-card--overdue': t.past_due }">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <b>{{ t.driver_name }}</b> — {{ t.topic }}
                    <div class="cp-small text-muted">{{ t.why }}<span v-if="t.event_id"> · event #{{ t.event_id }}</span></div>
                  </div>
                  <span class="cp-status-chip" :class="'cp-status-chip--' + t.status">
                    <i class="fa" :class="statusIcon(t.status)"></i>
                    {{ statusLabel(t.status) }}
                  </span>
                </div>
                <div class="d-flex justify-content-between align-items-center mt-2">
                  <span class="cp-small" :class="t.past_due ? 'text-danger' : 'text-muted'">
                    due {{ formatDate(t.due_date) }}<span v-if="t.escalated" class="font-weight-bold"> · ESCALATED</span><span v-else-if="t.past_due"> · {{ t.days_overdue }}d overdue</span>
                  </span>
                  <b-button size="sm" :variant="isOpenStatus(t.status) ? 'primary' : 'outline-secondary'"
                            @click="openSignOff(t)">
                    {{ isOpenStatus(t.status) ? 'Sign off…' : 'View / add note' }}
                  </b-button>
                </div>
              </b-card>
            </div>
          </div>
      </div>
    </div>

    <!-- sign-off / add-note modal, shared with the Coaching Board -->
    <coaching-signoff-modal ref="signoff" :tenant="tenant" @changed="loadTickets" />

    <!-- manual "+ Add ticket" create, shared with the Coaching Board -->
    <coaching-add-ticket-modal ref="addTicket" :tenant="tenant"
                               :tickets="tickets" :settings="settings"
                               @created="loadTickets" />

  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { get_coaching_tickets } from '../networking/apis'
import { isFeatureEnabled } from '../utils/alerts_access'
import { resolveActingTenant, tenantDisplayName } from '../utils/tenant_context'
import { OPEN_STATUSES, statusLabel, statusIcon, sourceLabel } from '../utils/coaching_labels'
import CoachingPortalHelp from './coaching_portal_help.vue'
import CoachingSignoffModal from './coaching_signoff_modal.vue'
import CoachingAddTicketModal from './coaching_add_ticket_modal.vue'

export default {
  name: 'coaching_portal',
  components: { CoachingPortalHelp, CoachingSignoffModal, CoachingAddTicketModal },
  data() {
    return {
      loading: false,
      error: null,
      tickets: [],
      summary: {},

      // the search/filter bar. 'queue' (open + scheduled — the work) is the
      // landing default; everything else starts wide open.
      filterStatus: 'queue',
      filterKind: '',
      filterDriver: '',
      filterFrom: '',
      filterTo: '',
      filterSearch: '',
      datePresets: [7, 30, 90],
      filtersExpanded: false,

      // R3: tenant SLA settings ride back on the list response (read-only
      // here — editing lives on the Coaching Dashboard)
      settings: null,

      // one scope value drives every ticket tab: '' = everyone, MINE = the
      // signed-in user, any other string = that coach's username. the
      // default is decided once after the first load (mine when I have
      // open work).
      assigneeFilter: '',
      MINE: '__mine__',
      scopeInitialized: false,

      isMobile: window.innerWidth <= 768,
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    tenant() {
      return resolveActingTenant(this.user, this.$route.query.tenant)
    },
    // ops-family convention (utils/tenant_context.js): whenever the page
    // renders a different tenant than the navbar selection, say so
    actingTenantDiffers() {
      return String(this.tenant) !== String(this.user.tenant)
    },
    actingTenantLabel() {
      return tenantDisplayName(this.user, this.tenant)
    },
    coachingTickets() {
      return this.tickets.filter(t => t.kind !== 'recognition')
    },
    myTickets() {
      return this.coachingTickets.filter(t => t.assigned_to === this.user.username)
    },
    myOpenCount() {
      return this.myTickets.filter(t => OPEN_STATUSES.includes(t.status)).length
    },
    statusOptions() {
      const count = s => this.tickets.filter(t => t.status === s).length
      const queueCount = this.tickets.filter(t => OPEN_STATUSES.includes(t.status)).length
      return [
        // "Queue", not "Open": it covers open+scheduled (the work), while
        // the Open tile above counts status open only — giving the two
        // different names keeps the two different numbers honest
        { value: 'queue', text: `Queue — open + scheduled (${queueCount})` },
        { value: '', text: `All statuses (${this.tickets.length})` },
        { value: 'open', text: `Open (${count('open')})` },
        { value: 'scheduled', text: `Scheduled (${count('scheduled')})` },
        { value: 'completed', text: `Completed (${count('completed')})` },
      ]
    },
    kindOptions() {
      return [
        { value: '', text: 'All types' },
        { value: 'coaching', text: `Coaching (${this.coachingTickets.length})` },
        { value: 'recognition', text: `Recognition (${this.recognitionTickets.length})` },
      ]
    },
    driverOptions() {
      const by = {}
      this.tickets.forEach(t => {
        if (!t.driver_name) return
        by[t.driver_name] = (by[t.driver_name] || 0) + 1
      })
      const drivers = Object.entries(by)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, n]) => ({ value: name, text: `${name} (${n})` }))
      return [{ value: '', text: 'All drivers' }, ...drivers]
    },
    // the coach dropdown lists everyone who actually holds tickets, with
    // their open workload — more useful than the full contacts feed for
    // "show me what's on Evan's plate"
    assigneeOptions() {
      const by = {}
      this.tickets.forEach(t => {
        if (!t.assigned_to) return
        const row = by[t.assigned_to] = by[t.assigned_to] || {
          name: t.assigned_to_name || t.assigned_to, open: 0,
        }
        if (OPEN_STATUSES.includes(t.status)) row.open += 1
      })
      const coaches = Object.entries(by)
        .filter(([username]) => username !== this.user.username)
        .sort((a, b) => a[1].name.localeCompare(b[1].name))
        .map(([username, row]) => ({
          value: username,
          text: `${row.name}${row.open ? ` (${row.open} open)` : ''}`,
        }))
      return [
        { value: '', text: 'Everyone' },
        { value: this.MINE, text: `Mine (${this.myOpenCount} open)` },
        ...coaches,
      ]
    },
    // active filter dimensions for the mobile toggle badge (search excluded)
    activeFilterCount() {
      let n = 0
      if (this.filterStatus !== 'queue') n++
      if (this.filterKind) n++
      if (this.filterDriver) n++
      if (this.assigneeFilter) n++
      if (this.filterFrom || this.filterTo) n++
      return n
    },
    filtersDirty() {
      return this.filterStatus !== 'queue' || this.filterKind !== ''
        || this.filterDriver !== '' || this.assigneeFilter !== ''
        || this.filterFrom !== '' || this.filterTo !== '' || this.filterSearch !== ''
    },
    recognitionTickets() {
      return this.tickets.filter(t => t.kind === 'recognition')
    },
    slaEscalateDays() {
      return (this.settings && this.settings.escalate_after_overdue_days) || 7
    },
    filteredTickets() {
      const assignee = this.assigneeFilter === this.MINE ? this.user.username : this.assigneeFilter
      const q = this.filterSearch.trim().toLowerCase()
      // "#12" (or a bare number) searches the ticket id exactly
      const idQuery = /^#?\d+$/.test(q) ? Number(q.replace('#', '')) : null
      return this.tickets.filter(t => {
        if (this.filterStatus === 'queue') {
          if (!OPEN_STATUSES.includes(t.status)) return false
        } else if (this.filterStatus && t.status !== this.filterStatus) return false
        if (this.filterKind && (t.kind || 'coaching') !== this.filterKind) return false
        if (this.filterDriver && t.driver_name !== this.filterDriver) return false
        if (assignee && t.assigned_to !== assignee) return false
        // raised-date range compares calendar days in the created_at ISO
        // string, so the boundary days are inclusive on both ends
        const created = (t.created_at || '').slice(0, 10)
        if (this.filterFrom && (!created || created < this.filterFrom)) return false
        if (this.filterTo && (!created || created > this.filterTo)) return false
        if (q) {
          if (idQuery !== null) return t.id === idQuery
          const hay = [t.driver_name, t.topic, t.why, t.assigned_to_name, t.assigned_to]
            .filter(Boolean).join(' ').toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    },
    // event links render only when the ops_desk router guard would admit
    // this viewer — the full guard, role AND feature flag, because a
    // link that silently bounces to the dashboard reads as a bug
    canViewOpsDesk() {
      if (this.user.role === 'admin') return true
      const f = (this.user.features && this.user.features[this.user.tenant]) || {}
      return ['customer-admin', 'tbn-admin'].includes(this.user.role)
        && isFeatureEnabled(f.ops_desk_enabled)
    },
    ticketFields() {
      // column layout mirrors the erm event log: fixed chip columns, a
      // flexible snippet column that absorbs leftover width, and a
      // shrink-to-fit driver column
      return [
        { key: 'id', label: 'ID', thStyle: { width: '56px' } },
        { key: 'kind', label: 'Type', thStyle: { width: '116px' } },
        { key: 'driver_name', label: 'Driver', thClass: 'cp-driver-cell', tdClass: 'cp-driver-cell' },
        { key: 'topic', label: 'Topic', tdClass: 'cp-snippet-cell' },
        { key: 'source', label: 'Source', thStyle: { width: '118px' } },
        { key: 'assigned_to_name', label: 'Assigned', thStyle: { width: '120px' } },
        { key: 'due_date', label: 'Due', thStyle: { width: '104px' } },
        { key: 'status', label: 'Status', thStyle: { width: '186px' } },
        { key: 'actions', label: '', thStyle: { width: '156px' }, tdClass: 'cp-actions-cell' },
      ]
    },
  },
  watch: {
    // the navbar tenant switcher mutates user.tenant in place without
    // remounting the page (ops_desk.vue precedent). per-tenant ticket ids
    // collide, and a sign-off is permanent — so on any tenant change, drop
    // every trace of the old tenant's queue before it can be acted on.
    tenant() {
      this.$refs.signoff.resetTenantScope()
      this.$refs.addTicket.resetTenantScope()
      this.tickets = []
      this.summary = {}
      this.settings = null
      // filters and the scope default are tenant-scoped too
      this.clearFilters()
      this.scopeInitialized = false
      this.loadTickets()
    },
  },
  created() {
    // ?status= deep link (the home-page widget's chips): preselect the Status
    // filter. validated against the real options so a bogus value can't leave
    // the page showing an empty list with no visible reason.
    const wantedStatus = this.$route.query.status
    if (wantedStatus != null && ['queue', '', 'open', 'scheduled', 'completed'].includes(String(wantedStatus))) {
      this.filterStatus = String(wantedStatus)
    }
    // ?assignee=everyone (the home widget's chips): its counts are
    // fleet-wide, so without this the page's own auto-scope to Mine would
    // land a chip reading 12 on a list of 3 with no visible reason.
    // scopeInitialized stops loadTickets re-applying that default.
    const wantedAssignee = this.$route.query.assignee
    if (wantedAssignee === 'everyone' || wantedAssignee === 'mine') {
      this.assigneeFilter = wantedAssignee === 'mine' ? this.MINE : ''
      this.scopeInitialized = true
    }
    // ?ticket_id= deep link (the erm event chip uses it): open the ticket's
    // sign-off modal once the queue has loaded
    this.loadTickets().then(() => {
      const wanted = Number(this.$route.query.ticket_id)
      if (!wanted) return
      // ticket ids are per-tenant counters: if resolveActingTenant fell back
      // from the link's tenant, the same number would be a DIFFERENT ticket
      const routeTenant = this.$route.query.tenant
      if (routeTenant && String(routeTenant) !== String(this.tenant)) {
        this.$bvToast.toast(
          'This link referred to another fleet’s coaching queue — showing your own queue instead.',
          { title: 'Coaching Portal', variant: 'warning', autoHideDelay: 6000 },
        )
        return
      }
      const ticket = this.tickets.find(t => t.id === wanted)
      if (ticket) this.openSignOff(ticket)
    })
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() { this.isMobile = window.innerWidth <= 768 },
    isOpenStatus(status) { return OPEN_STATUSES.includes(status) },
    statusLabel, statusIcon, sourceLabel,
    formatDate(d) { return d ? moment(d.slice(0, 10)).format('MMM D, YYYY') : '—' },
    rowClass(item) {
      if (!item) return ''
      if (item.escalated) return 'cp-row--escalated'
      if (item.past_due) return 'cp-row--overdue'
      if (!OPEN_STATUSES.includes(item.status)) return 'cp-row--closed'
      return ''
    },
    apiErrorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback
    },

    async loadTickets() {
      this.loading = true
      this.error = null
      try {
        const resp = await get_coaching_tickets(this.tenant)
        const payload = (resp.value && resp.value.data) || {}
        this.tickets = payload.tickets || []
        this.summary = payload.summary || {}
        if (payload.settings) this.settings = payload.settings
        if (!this.scopeInitialized) {
          this.assigneeFilter = this.myOpenCount > 0 ? this.MINE : ''
          this.scopeInitialized = true
        }
      } catch (e) {
        this.error = this.apiErrorMessage(e, 'Failed to load the coaching queue.')
      } finally {
        this.loading = false
      }
    },




    openSignOff(ticket) {
      this.$refs.signoff.open(ticket)
    },
    // a preset is "active" when the pickers hold exactly its range — no
    // separate state to fall out of sync when a date is edited by hand
    presetActive(days) {
      return this.filterFrom === moment().subtract(days, 'days').format('YYYY-MM-DD')
        && (this.filterTo === '' || this.filterTo === moment().format('YYYY-MM-DD'))
    },
    setDatePreset(days) {
      if (this.presetActive(days)) {
        this.filterFrom = ''
        this.filterTo = ''
        return
      }
      this.filterFrom = moment().subtract(days, 'days').format('YYYY-MM-DD')
      this.filterTo = ''
    },
    clearFilters() {
      this.filterStatus = 'queue'
      this.filterKind = ''
      this.filterDriver = ''
      this.assigneeFilter = ''
      this.filterFrom = ''
      this.filterTo = ''
      this.filterSearch = ''
    },
  },
}
</script>

<style scoped>
/* the app shell centers text (#app) — this page is left-aligned, so inline
   controls (the mobile Filters toggle) sit left like everything else */
.cp-root { padding-bottom: 24px; text-align: left; }
.cp-stale { opacity: 0.6; }
.cp-tenant-note { margin-top: 10px; font-size: 0.85em; color: var(--fi-text-muted, #6b7280); }
.cp-empty { margin: 40px 0; }
/* search/filter bar: the ERM Event Log's od-filter-row vocabulary, cp-scoped */
.cp-filter-row {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 8px 12px;
  margin-top: 14px; padding-bottom: 8px;
  border-bottom: 1px solid #eef0f3;
  font-size: 13px;
}
.cp-filter-field { display: inline-flex; align-items: center; gap: 4px; }
.cp-filter-lbl {
  font-size: 10.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .4px;
  color: #6b7280; white-space: nowrap;
}
.cp-filter-select { width: 190px; font-size: 13px; }
.cp-filter-select--narrow { width: 140px; }
.cp-filter-date { width: 140px; font-size: 13px; }
/* empty date input: placeholder-strength ink (see cp-date-ghost note in
   the template). editing restores full ink so segment entry stays legible. */
.cp-filter-date.cp-date-ghost { color: #9aa4b1; }
.cp-filter-date.cp-date-ghost:focus { color: inherit; }
/* 7d/30d/90d one-tap presets (od-chip recipe, compact) */
.cp-preset-chip {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 3px 10px; font-size: 12px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
}
.cp-preset-chip + .cp-preset-chip { margin-left: 4px; }
.cp-preset-chip:hover { background: #eef4fb; border-color: #b8c8da; }
.cp-preset-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
/* search pill anchored to the right of the filter row (od-search recipe) */
.cp-search {
  margin-left: auto;
  position: relative;
  display: inline-flex; align-items: center;
  flex: 0 1 240px; min-width: 170px;
}
.cp-search-icon {
  position: absolute; left: 11px;
  color: #9aa3ad; font-size: 12px; pointer-events: none;
}
.cp-search-input {
  width: 100%;
  padding: 5px 26px 5px 28px;
  font-size: 13px;
  border: 1px solid var(--fi-line, #d6dde6); border-radius: 999px;
  background: var(--fi-surface, #fff); color: var(--fi-text, #1d1d1f);
  transition: border-color .12s, box-shadow .12s;
}
.cp-search-input::placeholder { color: #9aa3ad; }
.cp-search-input:focus {
  outline: none;
  border-color: var(--fi-ops-accent, #3a6e99);
  box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.2);
}
.cp-search-clear {
  position: absolute; right: 6px;
  background: none; border: none; padding: 2px 5px;
  color: #9aa3ad; cursor: pointer; font-size: 12px; line-height: 1;
}
.cp-search-clear:hover { color: #4b5563; }
.cp-results-row {
  display: flex; align-items: center; gap: 10px;
  margin-top: 6px;
}
.cp-results-count { font-size: 12px; color: #6b7280; font-variant-numeric: tabular-nums; }
.cp-clear-filters {
  background: none; border: none; padding: 0;
  font-size: 12px; font-weight: 600; color: var(--fi-ops-accent-strong, #2d5a80);
  cursor: pointer;
}
.cp-clear-filters:hover { text-decoration: underline; }
.cp-you-chip {
  display: inline-block; padding: 1px 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
}
.cp-small { font-size: 0.85em; }
.cp-mobile-card--overdue { border-left: 3px solid #dc3545; }

/* queue table: the ERM Event Log visual system (ops_desk.vue), cp-scoped */
.cp-table-inline >>> .table-responsive { overflow-x: auto; overflow-y: hidden; }
.cp-table-inline >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.cp-table-inline >>> .table.b-table th,
.cp-table-inline >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.cp-table-inline >>> .table.b-table th:first-child,
.cp-table-inline >>> .table.b-table td:first-child { padding-left: 12px !important; }
.cp-table-inline >>> .table.b-table th:last-child,
.cp-table-inline >>> .table.b-table td:last-child { padding-right: 12px !important; }
.cp-table-inline >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  position: sticky; top: 0; z-index: 2;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.cp-table-inline >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; cursor: pointer; }
.cp-table-inline >>> td.cp-snippet-cell { max-width: 0; }
.cp-table-inline >>> .cp-driver-cell { white-space: nowrap; width: 1%; }
.cp-table-inline >>> td.cp-actions-cell { white-space: nowrap; text-align: right !important; }
.cp-table-inline >>> tr.cp-row--escalated > td:first-child { box-shadow: inset 3px 0 0 0 #d62f2f; }
.cp-table-inline >>> tr.cp-row--overdue > td:first-child { box-shadow: inset 3px 0 0 0 #e8912d; }
.cp-table-inline >>> tr.cp-row--closed td { opacity: 0.72; }
.cp-snippet {
  display: block; color: #374151;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cp-id-link {
  background: none; border: none; padding: 0;
  font-size: 13px; color: var(--fi-ops-accent-strong); text-decoration: underline;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}
.cp-id-link:hover { color: var(--fi-ops-accent-deep); }
.cp-list-dt {
  display: inline-block; white-space: nowrap;
  font-variant-numeric: tabular-nums; color: #374151;
}
.cp-list-dt--overdue { color: #b91c1c; font-weight: 600; }
/* chip families copied from the erm list so a row carries one chip system.
   coaching borrows the incident orange (corrective, not catastrophic);
   recognition borrows the kudos green verbatim. */
.cp-type-chip, .cp-status-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px;
  font-size: 11px; line-height: 1.4; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.cp-type-chip .fa, .cp-status-chip .fa { font-size: 10px; line-height: 1; }
.cp-type-chip--coaching    { background: #fee4d1; color: #b85a1e; border-color: #f0c7a4; }
.cp-type-chip--recognition { background: #ddf3e3; color: #146230; border-color: #b7e2c4; }
.cp-status-chip--open         { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }
.cp-status-chip--scheduled    { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.cp-status-chip--completed    { background: #e7f8ed; color: #166534; border-color: #b5e3c5; }
/* acknowledged-in-person marker: an attribute of the signature now, so it
   sits beside the status chip rather than replacing it */
.cp-ack-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px;
  font-size: 11px; line-height: 1.4; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
  background: #e7f1ff; color: #0b3d91; border-color: #bcd8fb;
}
.cp-cat-chip {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11.5px; line-height: 1.4; font-weight: 600;
  border-radius: 4px;
  background: #eef4fb; color: #1f5582; border: 1px solid #d6e1ee;
  white-space: nowrap;
}
.cp-sev-chip {
  display: inline-flex; align-items: center;
  padding: 2px 8px;
  font-size: 11px; line-height: 1.4; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.cp-sev-chip--high { background: #fbe3e5; color: #b91c1c; border-color: #f5c0c4; }


/* mobile filters toggle (the ERM Event Log pattern): hidden above 600px */
.cp-mobile-only { display: none; }
.cp-filter-toggle {
  align-items: center; gap: 7px;
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6); border-radius: 999px;
  padding: 7px 14px; margin: 14px 0 0;
  font-size: 13.5px; font-weight: 600; color: var(--fi-text, #374151);
  cursor: pointer; transition: background .12s, border-color .12s;
}
.cp-filter-toggle:focus-visible { outline: 2px solid var(--fi-ops-accent, #3a6e99); outline-offset: 2px; }
.cp-filter-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  font-size: 11px; font-weight: 700; line-height: 1;
}
@media (max-width: 600px) {
  /* full-width search leading the filter row; 16px to dodge iOS focus-zoom */
  .cp-search { margin-left: 0; flex: 1 0 100%; order: -1; }
  .cp-search-input { font-size: 16px; }
  .cp-filter-field { flex: 1 1 auto; }
  .cp-filter-date, .cp-filter-select { width: 100%; font-size: 16px; }
  /* mobile-only affordances turn on here */
  .cp-mobile-only { display: inline-flex; }
  /* collapsed: every filter control hides except the search pill, so the
     phone opens straight to rows. the toggle above flips it open. */
  .cp-filters-collapsed .cp-filter-field { display: none; }
}

/* mobile: the header stays one row — title and actions inline. overrides
   FiPageRail's own 600px rule that wraps actions onto a full-width second
   row (fine for pages with a toolbar; a waste for one info icon). */
@media (max-width: 600px) {
  .cp-rail.fi-pr-rail { flex-wrap: nowrap; row-gap: 0; }
  .cp-rail >>> .fi-pr-actions { width: auto; }
  /* the add button drops to icon-only beside the title (od-btn-label rule) */
  .cp-btn-label { display: none; }
}
</style>

